import { useWorkareaPricingInvalidator } from '../workareas/workareaPricingQueries'
import { useMemo } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"
import * as invoicingApi from "./invoicingApi"
import { notification } from "antd"
import { useJobItemsInvalidator } from "../../components/Items/itemQueries"
import { useResolvedTaxRateInvalidator } from "./resolvedTaxRateQueries"
import { useWorkareaGroupsInvalidator } from "../workareas/groupsQueries"

export const useInvoiceSummariesForJobQuery = (jobId: number) => {
  return useQuery(['invoicing', 'invoice-summaries', { jobId }], {
    queryFn: () => invoicingApi.getInvoiceSummariesForJob(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading invoice summaries.' })
    }
  })
}

export const useTransactionsForJobQuery = (jobId: number) => {
  return useQuery(['invoicing', 'transactions', { jobId }], {
    queryFn: () => invoicingApi.getTransactionsForJob(jobId),
    enabled: !!jobId,
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    onError: () => {
      notification.error({ message: 'An error occurred while loading transactions.' })
    }
  })
}

export const useInvoiceDetailsQuery = (invoiceId: string) => {
  return useQuery(['invoicing', 'invoice-details', { invoiceId }], {
    queryFn: () => invoicingApi.getInvoiceDetails(invoiceId),
    onError: () => {
      notification.error({ message: 'An error occurred while loading invoice data.' })
    },
    staleTime: 30 * 1000,
    enabled: !!invoiceId
  })
}

export const useEmailInvoiceMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.emailInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while sending the email.'
      notification.error({ message })
    }
  })
}

export const useExportInvoiceMutation = (onSuccess: (qboInvoiceUrl: string) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.exportInvoice,
    onSuccess: x => {
      queryClient.invalidateQueries(['invoicing'])
      onSuccess(x.qboInvoiceUrl)
    },
    onError: (error: any) => {
      queryClient.invalidateQueries(['invoicing'])
      const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while sending the email.'
      notification.error({ message })
    }
  })
}

type DeleteInvoiceMutationData = {
  invoiceId: string
  deleteFromQuickBooks: boolean
  projectId: number
}

export const useDeleteInvoiceMutation = () => {
  const itemsInvalidator = useJobItemsInvalidator()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, deleteFromQuickBooks }: DeleteInvoiceMutationData) => invoicingApi.deleteInvoice(invoiceId, deleteFromQuickBooks),
    onSuccess: (_, data) => {
      itemsInvalidator.invalidateForJob(data.projectId)
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      queryClient.invalidateQueries(['invoicing'])
      const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while deleting the invoice.'
      notification.error({ message })
    }
  })
}

export const useCreateInvoiceMutation = (onSuccess: (data: invoicingApi.CreateInvoiceResponse) => void, onError: (error: unknown) => void) => {
  const itemsInvalidator = useJobItemsInvalidator()
  const workareaGroupsInvalidator = useWorkareaGroupsInvalidator()
  const resolvedTaxRateInvalidator = useResolvedTaxRateInvalidator()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.createInvoice,
    onSuccess: (response, request) => {
      itemsInvalidator.invalidateForJob(request.invoice.projectId)
      workareaGroupsInvalidator.invalidateForJob(request.invoice.projectId)
      queryClient.invalidateQueries(['invoicing'])
      onSuccess(response)
    },
    onError: (error: any, request) => {
      itemsInvalidator.invalidateForJob(request.invoice.projectId)
      workareaGroupsInvalidator.invalidateForJob(request.invoice.projectId)
      queryClient.invalidateQueries(['invoicing'])
      if (invoicingApi.isUnresolvableTaxRateError(error)) {
        resolvedTaxRateInvalidator.invalidateForJob(request.invoice.projectId)
      }

      if (window && !!window.Intercom) {
        const metadata = {
          message: error?.response?.data?.errors[0] ?? 'unknown error'
        }
        window.Intercom("trackEvent", "invoice-error", metadata);
      }
      if (error?.response?.data?.wasCreated === true) {
        notification.warning({
          message: 'The invoice was created but there was a problem sending it to QuickBooks:',
          description: error.response.data.errors.join('\n'),
          duration: 0
        })
      } else {
        const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while sending the email.'
        notification.error({ message })
      }
      onError(error)
    }
  })
}

const showErrorNotification = (message: string, error: any) => {
  if (error?.response?.data?.quickBooksError) {
    notification.error({
      message: 'QuickBooks Online reported an error:',
      description: error.response.data.quickBooksError,
      duration: 0
    })
    return
  }
  if (error?.response?.data?.errors) {
    notification.error({ message: error.response.data.errors.join(' ') })
    return
  }
  notification.error({ message })
}

export const useGetTaxCodesQuery = (enabled: boolean) => {
  return useQuery('taxCodes', invoicingApi.getTaxCodes, { enabled })
}

export const useCompletedWorkareaVisitsQuery = (jobId: number, enabled: boolean) => {
  return useQuery(['invoicing', 'completed-workarea-visits', jobId], {
    queryFn: () => invoicingApi.getCompletedWorkareaVisits(jobId),
    enabled
  })
}

export const useUpdateCompletedVisitPricingMutation = (onSuccess: () => void) => {
  const pricingInvalidator = useWorkareaPricingInvalidator()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.updateCompletedVisitPricing,
    onSuccess: () => {
      pricingInvalidator.invalidateAll()
      queryClient.invalidateQueries(['invoicing'])
      onSuccess()
    },
    onError: (error: any) => {
      queryClient.invalidateQueries(['invoicing'])
      showErrorNotification('An error occurred while trying to update completed visit prices.', error)
    }
  })
}

type QueryOptions = {
  disabled?: boolean
}

export const useGetPaymentStatusQuery = (invoiceId: string, options?: QueryOptions) => {
  return useQuery(['invoicing', 'payment-status', invoiceId], {
    queryFn: () => invoicingApi.getPaymentStatus(invoiceId),
    enabled: !!invoiceId && !options?.disabled
  })
}

export const useGetPaymentMethodsOnFileQuery = (customerId: number, options?: QueryOptions) => {
  return useQuery(['invoicing', 'payment-methods-on-file', customerId], {
    queryFn: () => invoicingApi.getPaymentMethodsOnFile(customerId),
    enabled: !options?.disabled
  })
}

export const usePayWithPaymentMethodOnFileMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.payWithPaymentMethodOnFile,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    }
  })
}

export const useMarkInvoicePaidManuallyMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.markInvoicePaidManually,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    }
  })
}

export const useMarkInvoiceNotPaidManuallyMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.markInvoiceNotPaidManually,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'An error occurred while marking the invoice not paid.'
      notification.error({ message })
    }
  })
}

export const useSendInvoiceMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.sendInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while sending the email.'
      notification.error({ message })
    }
  })
}

export const useSyncInvoiceToQuickBooksMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.syncInvoiceToQuickBooks,
    onSuccess: () => {
      queryClient.invalidateQueries(['invoicing'])
    },
    onError: (error: any) => {
      queryClient.invalidateQueries(['invoicing'])
      const message = error.response?.data?.errors?.join('\n') ?? 'An error occurred while syncing to QuickBooks.'
      notification.error({ message })
    }
  })
}

export const useGetPaymentCollectionLinkQuery = (customerId: number) => {
  return useQuery(['invoicing', 'payment-collection-link', customerId], {
    queryFn: () => invoicingApi.getPaymentCollectionLink(customerId),
  })
}

export const useAllInvoiceSummariesQuery = (request: invoicingApi.AllInvoicesRequest) => {
  return useQuery(['invoicing', 'all-invoice-summaries', request], {
    queryFn: () => invoicingApi.getAllInvoiceSummaries(request),
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading invoice summaries.' })
    }
  })
}

export const useFetchAllInvoiceIdsMutation = () => {
  return useMutation({
    mutationFn: invoicingApi.getAllInvoiceIds,
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'An error occurred while loading invoice IDs.'
      notification.error({ message })
    }
  })
}

export const useJobsForInvoicingQuery = (request: invoicingApi.GetJobsForInvoicingRequest) => {
  return useQuery(['invoicing', 'jobs-for-invoicing', { startDate: request.startDate, endDate: request.endDate }], {
    queryFn: () => invoicingApi.getJobsForInvoicing(request),
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading jobs for invoicing.' })
    }
  })
}

const readyToInvoiceQueryKey = ['invoicing', 'ready-to-invoice-jobs']

export const useReadyToInvoiceJobsInvalidator = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries(readyToInvoiceQueryKey)
}

export const useReadyToInvoiceJobsQuery = (request: invoicingApi.GetReadyToInvoiceJobsRequest) => {
  return useQuery([...readyToInvoiceQueryKey, request], {
    queryFn: () => invoicingApi.getReadyToInvoiceJobs(request),
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading jobs ready to invoice.' })
    }
  })
}

export const useBatchCreateInvoicesMutation = (
  onSuccess: (data: invoicingApi.BatchCreateResponse, request: invoicingApi.BatchCreateRequest) => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.batchCreateInvoices,
    onSuccess: (response, request) => {
      queryClient.invalidateQueries(['invoicing'])
      onSuccess(response, request)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'An error occurred while starting batch invoice creation.'
      notification.error({ message })
    }
  })
}

export const useBatchStatusQuery = (batchId: string | null) => {
  return useQuery(['invoicing', 'batch-status', batchId], {
    queryFn: () => invoicingApi.getBatchStatus(batchId!),
    enabled: !!batchId,
    refetchInterval: (data) => {
      if (!data || data.status === 'processing' || data.status === 'pending') return 2000
      return false
    }
  })
}

export const useActiveBatchQuery = () => {
  return useQuery(['invoicing', 'active-batch'], {
    queryFn: invoicingApi.getActiveBatch,
    refetchInterval: (data) => {
      if (data && (data.status === 'processing' || data.status === 'pending')) return 3000
      return false
    }
  })
}

const batchHistoryQueryKey = ['invoicing', 'batch-history']

export const useBatchHistoryQuery = (request: invoicingApi.BatchHistoryRequest) => {
  return useQuery([...batchHistoryQueryKey, request], {
    queryFn: () => invoicingApi.getBatchHistory(request),
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading batch history.' })
    }
  })
}

export const useBatchHistoryInvalidator = () => {
  const queryClient = useQueryClient()
  return useMemo(() => ({
    invalidate: () => queryClient.invalidateQueries(batchHistoryQueryKey)
  }), [queryClient])
}

export const useBatchJobsQuery = (batchId: string | null) => {
  return useQuery(['invoicing', 'batch-jobs', batchId], {
    queryFn: () => invoicingApi.getBatchJobs(batchId!),
    enabled: !!batchId,
    refetchOnWindowFocus: false,
    onError: () => {
      notification.error({ message: 'An error occurred while loading batch job details.' })
    }
  })
}

export const useBulkSendInvoicesMutation = (onSuccess: (data: invoicingApi.BatchCreateResponse) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.bulkSendInvoices,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['invoicing'])
      onSuccess(response)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'An error occurred while starting bulk send.'
      notification.error({ message })
    }
  })
}

export const useBulkDeleteInvoicesMutation = (onSuccess: (data: invoicingApi.BatchCreateResponse) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.bulkDeleteInvoices,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['invoicing'])
      onSuccess(response)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'An error occurred while starting bulk delete.'
      notification.error({ message })
    }
  })
}

export const useBulkSyncInvoicesMutation = (onSuccess: (data: invoicingApi.BatchCreateResponse) => void) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: invoicingApi.bulkSyncInvoices,
    onSuccess: (response) => {
      queryClient.invalidateQueries(['invoicing'])
      onSuccess(response)
    },
    onError: (error: any) => {
      const message = error.response?.data?.message ?? 'An error occurred while starting bulk sync.'
      notification.error({ message })
    }
  })
}
